import React, { useState, useEffect, useRef } from "react";

import General from "../utils/General";
import Backend from "../utils/Backend";

import TopBar from "./components/TopBar";
import Editor from "./components/editor/Editor";

import FilterEditorModal from "./components/modal/FilterEditorModal";

import ToolBar from "./components/ToolBar";
import Cropper from "./components/editor/Cropper";
import TestRetouchCanvas from "./components/editor/TestRetouchCanvas";

import useUndoRedo from "./components/useUndoRedo";

const history = [];
const IMAGE_BASE_URL = "https://d2db4y98rlegpn.cloudfront.net/images/customers";
const IMAGE = { original: `${IMAGE_BASE_URL}/image00007.jpeg` };

const TestPhotoEdit = (props) => {
  const {
    state,
    setState,
    undo,
    redo,
    isUndoPossible,
    isRedoPossible,
    pastStates,
    futureStates,
  } = useUndoRedo(null);

  const canvasRef = useRef(null);
  const cropperRef = useRef(null);

  const [data, setData] = useState(null);
  const [selectedTextColor, setSelectedTextColor] = useState(null);
  const [project, setProject] = useState(null);
  const [historyStep, setHistoryStep] = useState(null);
  const [inApp, setInApp] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showFilterEditor, setShowFilterEditor] = useState(false);
  const [showHSL, setShowHSL] = useState(false);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const base64Image = await fileToBase64(file);
    const metadata = await extractMetadata(file);

    console.log("meta data is ", metadata);

    const fileData = {
      base64_image: base64Image,
      base64_thumbnail: await createThumbnail(base64Image),
      metadata: metadata,
    };

    setupProjectFromImageData(fileData);
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const extractMetadata = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
          title: file.name,
        });
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const createThumbnail = async (base64Image) => {
    const maxSize = 150; // Max width or height for the thumbnail
    const img = new Image();
    return new Promise((resolve) => {
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const scaleFactor = Math.min(maxSize / img.width, maxSize / img.height);
        canvas.width = img.width * scaleFactor;
        canvas.height = img.height * scaleFactor;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg"));
      };
      img.src = base64Image;
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let inApp = params.get("inApp");
    const projectId = params.get("id");

    if (projectId) {
      setupProject(projectId);
    } else {
      window.onmessage = (event) => {
        handleEvent(event);
      };

      // Android sends via document: https://github.com/react-native-webview/react-native-webview/issues/2980#issue-1737934676
      document.addEventListener("message", (event) => {
        handleEvent(event);
      });

      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage("ready");
      }
    }

    if (inApp == null) {
      inApp = window.ReactNativeWebView != null;
    } else {
      inApp = inApp === "true";
    }

    setInApp(inApp);
  }, []);

  useEffect(() => {
    if (
      project?.id &&
      project?.current_state?.preview_image?.id !== state?.preview_image?.id
    ) {
      saveProject();
    }
  }, [state?.preview_image?.id]);

  const saveProject = () => {
    if (!project) {
      console.log("success: project");
      return;
    }

    setIsSaving(true);
    console.log("success: setIsSaving");
    let data = {
      state: {
        data: state,
        order: state.order,
        tool: state.currentTab,
        filter: state.filter.id,
        preview_image: state.preview_image?.id,
        retouched_image: state.retouched_image,
      },
    };

    Backend.updateProject(project, data)
      .then(() => {
        console.log("success: updateProject");
        setIsSaving(false);

        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage("project-saved");
        }
      })
      .catch((err) => {
        console.log("catch: err", err);
        setIsSaving(false);
        console.log("err", err);
      });
  };

  const setupProject = (id) => {
    Backend.getProject(id)
      .then((project) => {
        let projectState = getState(project);

        projectState.retouched_image = project.current_state?.retouched_image;

        updateState({ ...projectState });

        setProject(project);

        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage("project-loading");
        }
      })
      .catch((err) => {
        console.log("err", err);
      });
  };

  const handleEvent = (event) => {
    let data = JSON.parse(event.data);
    if (data?.type == "create_project") {
      setupProjectFromImageData(data);
    }
  };

  const setupProjectFromImageData = (fileData) => {
    if (data) {
      return;
    }

    let project = {
      original_image: {
        original: fileData.base64_image,
        width: fileData.metadata.width,
        height: fileData.metadata.height,
      },
    };
    // console.log("Project data is : ",project)

    let projectState = getState(project);

    project.original_image.thumbnail = fileData.base64_thumbnail;

    updateState({ ...projectState });
    setProject(project);

    setIsSaving(true);

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage("project-loading");
    }

    let file = General.dataURIToBlob(fileData.base64_image, "image/jpeg");
    Backend.uploadImage(file, fileData.metadata)
      .then((image) => {
        let projectData = {
          type: "photo",
          name: fileData.metadata.title,
          original_image: image.id,
          state: {
            data: {
              ...projectState,
              image,
              originalImage: image,
              preview_image: image,
            },
            order: 0,
            preview_image: image.id,
            filter: projectState.filter.id,
          },
        };

        // setting manually to avoid triggering history
        setData((current) => {
          let newData = { ...current };
          newData.image = image;
          newData.originalImage = image;
          newData.preview_image = image;
          setState(newData);
          return newData;
        });

        return Backend.createProject(projectData);
      })
      .then((project) => {
        setIsSaving(false);
        setProject(project);
      })
      .catch((error) => {
        console.log("error", error);
      });
  };

  const getSize = (image) => {
    let maxSize = 2048;

    if (image.width <= maxSize && image.height <= maxSize) {
      return { width: image.width, height: image.height };
    }

    if (image.width > image.height) {
      let newWidth = maxSize;

      let ratio = image.width / maxSize;

      let newHeight = Math.round(image.height / ratio);

      return { width: newWidth, height: newHeight };
    }

    let newHeight = maxSize;

    let ratio = image.height / maxSize;

    let newWidth = Math.round(image.width / ratio);

    return { width: newWidth, height: newHeight };
  };

  const getState = (project) => {
    if (project.current_state?.data?.image) {
      return project.current_state?.data;
    }

    let image = project.original_image || IMAGE;
    console.log(image.thumbnail);

    if (image.thumbnail) {
      image.original = image.thumbnail;

      let size = getSize(image);

      image.original = image.original.replace(
        "400x225",
        `${size.width}x${size.height}`
      );
    }

    return {
      image,
      originalImage: image,
      adjustments: {
        brightness: 0,
        contrast: 0,
        saturation: 0,
        exposure: 0,
        highlights: 1,
        hsl: {
          hue: 0,
          saturation: 0,
          lightness: 0,
        },
      },
      filter: {
        id: 1,
        type: "Lightroom",
        key: "none",
        name: "None",
        intensity: 1.0,
        data: {
          type: "Lightroom",
          name: "None",
        },
      },
      text: {
        value: null,
        size: "150",
        color: "black",
        backgroundColor: "white",
        font: "Ruda",
        left: 0,
        top: 0,
        width: 0,
        height: 0,
        scaleX: 1,
        scaleY: 1,
      },
      frame: "none",
      transform: {
        rotation: 0,
        flipX: false,
      },
      aspectRatio: null,
      crop: null,
      currentTab: "filter",
      order: 0,
    };
  };

  const updateHistory = useRef(
    General.debounce(
      (state) => {
        setState(state);
      },
      1000,
      false
    )
  ).current;

  const onFilterEdited = useRef(
    General.debounce(
      (filter) => {
        if (filter.data.intensity == 1 || filter.data.intensity == 0.8) {
          return;
        }
        Backend.addEditFilter({ ...filter });
      },
      1000,
      false
    )
  ).current;

  const updateState = (newData) => {
    setData((current) => {
      let updatedData = {
        ...current,
        ...newData,
        order: state?.order ? state.order + 1 : 1,
      };
      updateHistory(updatedData);
      return updatedData;
    });
  };

  const onUndoClicked = () => {
    setData(pastStates[pastStates.length - 1]);

    undo();
  };

  const onRedoClicked = () => {
    setData(futureStates[0]);

    redo();
  };

  const onApplyClicked = () => {
    if (cropperRef.current) {
      cropperRef.current.applyChanges();
    }
    // TODO: trigger applyChanges from Cropper.js here
  };
  const handleTextChange = General.debounce(
    (text) => {
      updateState({ text });
    },
    300,
    false
  );

  const handleSave = () => {
    if (canvasRef.current) {
      canvasRef.current.saveImage();
    }
  };

  const renderPreview = () => {
    if (!data || !project) {
      return;
    }

    let retouchedImage = data.retouched_image || data.image;
    if (data.currentTab === "retouch") {
      return (
        <TestRetouchCanvas
          state={data}
          image={retouchedImage}
          onUpdated={(state) => {
            updateState({ ...state, tool: data.currentTab });
          }}
        />
      );
    } else if (data.currentTab === "straighten" || data.currentTab === "crop") {
      return (
        <Cropper
          ref={cropperRef}
          key={retouchedImage.original}
          state={data}
          image={retouchedImage}
          onUpdated={(state) => {
            updateState(state);
          }}
        />
      );
    }
    return (
      <Editor
        ref={canvasRef}
        state={data}
        image={retouchedImage}
        key={data.retouched_image?.id}
        onTextChange={(text) => handleTextChange(text)}
        onImageSaved={(image) => {
          updateState({ preview_image: image });
        }}
        onColorSelected={(color) => {
          data.text.color = color;
          setData(data);
        }}
      />
    );
  };

  let pageCSS = "pages photo-edit ";

  return (
    <div className="pages photo-edit">
      {!project ? (
        <div className="file-picker-container">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="file-input"
          />
          <p>Select an image to start editing</p>
        </div>
      ) : (
        <>
          <TopBar
            onSaveClicked={() => {
              setIsSaving(true);
              handleSave();
            }}
            isSaving={isSaving}
            addToPlanner={() => {}}
            currentTab={data.currentTab}
          />
          <div
            id="previewContainer"
            className="d-flex flex-column justify-content-between main"
          >
            {renderPreview()}
            <ToolBar
              activeTab={data.currentTab || "filter"}
              state={data}
              aspectRatio={data.aspectRatio}
              onStateUpdated={(state, action) => {
                updateState(state);
              }}
              onTabChanged={(currentTab) => {
                updateState({ currentTab });
              }}
              onFilterUpdated={(filter) => {
                updateState({ filter, tool: "filter" });
                onFilterEdited(filter);
              }}
              onAspectRatioChanged={(aspectRatio) => {
                updateState({ aspectRatio, tool: "" });
              }}
              onTextUpdated={(text) => {
                updateState({ text, tool: "text" });
              }}
              onFrameUpdated={(frame) => {
                updateState({ frame, tool: "frame" });
              }}
              onTransformUpdated={(transform) => {
                updateState({ transform, tool: "adjust" });
              }}
              onFilterSettingsClicked={() => setShowFilterEditor(true)}
              showHSL={showHSL}
              setShowHSL={setShowHSL}
              onHSLClicked={() => setShowHSL(true)}
              undoDisabled={isUndoPossible}
              redoDisabled={isRedoPossible}
              onUndoClicked={onUndoClicked}
              onRedoClicked={onRedoClicked}
              onApplyClicked={onApplyClicked}
            />
          </div>
          {showFilterEditor && (
            <FilterEditorModal
              show={showFilterEditor}
              filter={data.filter}
              onHide={() => setShowFilterEditor(false)}
              onUpdated={(filter) => {
                updateState({ filter });
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default TestPhotoEdit;
